import { useCallback, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import SystemAlert from '../../../components/modal/SystemAlert';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import { useWithdrawApplyingOrderMutation } from '../../../slices/ordersApiSlice';
import { useWithdrawApprovalApplicationMutation } from '../../../slices/approvalsApiSlice';
import { canWithdrawApplyingOrder, canWithdrawApproval } from '../../../utils/approval/canWithdrawApplication';
import { markLocalOrderMutation } from '../../../utils/order/orderLocalMutationGuard';
import PendingListStatusBadge, { PendingThemeBadge } from './common/PendingListStatusBadge';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight } from './common/PendingKeywordHighlight';
import formatCurrency from '../../../utils/formatCurrency';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import { buildPendingApplyAtColumn, getPendingApplyAt } from './common/pendingApplyAtColumn';
import { getApprovalTriggerSummaryLines, getPendingApprovalKind } from './common/pendingApprovalTriggerSummary';
import { getCityNameByCode } from '../../../utils/cityUtils';
import { getPendingListApplicant, isPendingListApplicantHighlighted } from '../../../utils/approval/approvalListHub';
import { getPendingApprovalStatusLabel, getPendingListStatusBadgeProps } from './common/pendingRowStatusDisplay';
import { PENDING_TAB_BADGE, PENDING_TABLE_PER_PAGE } from './common/pendingTableConfig';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import { usePendingSearch } from './common/PendingSearchContext';
import ApplyingModal from './modal/ApplyingModal';
import CustomerApplyInfoModal from './modal/CustomerApplyInfoModal';
import FinalCostApplyInfoModal from './modal/FinalCostApplyInfoModal';
import QuotaApplyInfoModal from './modal/QuotaApplyInfoModal';

const isCustomerOrQuotaEntry = (row) => row?.entryKind === 'customer' || row?.entryKind === 'quota';
const isStandaloneApprovalEntry = (row) => isCustomerOrQuotaEntry(row) || row?.entryKind === 'finalCost';

const ApplyingOrderPendings = () => {
	const { userInfo } = useSelector((state) => state.auth);
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const [customerApplyInfo, setCustomerApplyInfo] = useState(null);
	const [quotaApplyInfo, setQuotaApplyInfo] = useState(null);
	const [finalCostApplyInfo, setFinalCostApplyInfo] = useState(null);
	const [withdrawTarget, setWithdrawTarget] = useState(null);
	const { applyingOrders = [], isLoadingApplyingOrders, setCurrentOrderOIdState, refetchApplyingOrders, order } = useOrderContext();
	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(applyingOrders);
	const { initialApplyingPageIndex } = usePendingSearch();
	const [withdrawApplyingOrder, { isLoading: isWithdrawingOrder }] = useWithdrawApplyingOrderMutation();
	const [withdrawApprovalApplication, { isLoading: isWithdrawingApproval }] = useWithdrawApprovalApplicationMutation();

	const restoredPageRef = useRef(initialApplyingPageIndex);
	const restoredPageIndex =
		Number.isInteger(restoredPageRef.current) && restoredPageRef.current >= 0 ? restoredPageRef.current : 0;

	const displayOrders = useMemo(() => {
		const list = Array.isArray(filteredOrders) ? [...filteredOrders] : [];
		list.sort((a, b) => {
			const aTime = getPendingApplyAt(a);
			const bTime = getPendingApplyAt(b);
			return new Date(bTime || 0) - new Date(aTime || 0);
		});
		return list;
	}, [filteredOrders]);

	const openCustomerApplyInfo = useCallback((row) => {
		if (!row) return;
		setCustomerApplyInfo({
			row,
			approval: row.approval || {
				title: row.title,
				status: row.approvalStatus,
				detail: { newCustomerConcatName: row.customerInfo?.customerName || '' },
				applicant: row.createdUser,
				assistant: row.assistantUser,
				_id: row._id,
			},
		});
	}, []);

	const openQuotaApplyInfo = useCallback((row) => {
		if (!row) return;
		setQuotaApplyInfo({
			row,
			approval: row.approval || {
				title: row.title,
				status: row.approvalStatus,
				detail: { customerName: row.customerInfo?.customerName || '' },
				applicant: row.createdUser,
				assistant: row.assistantUser,
				_id: row._id,
			},
		});
	}, []);

	const openFinalCostApplyInfo = useCallback((row) => {
		if (!row) return;
		setFinalCostApplyInfo({
			row,
			approval: row.approval || {
				title: row.title,
				status: row.approvalStatus,
				detail: { orderId: row.orderId },
				applicant: row.createdUser,
				assistant: row.assistantUser,
				_id: row._id,
			},
		});
	}, []);

	const openApplyingModal = useCallback(
		(orderMongoId) => {
			if (!orderMongoId) return;
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(orderMongoId);
			socket.emit('join order', orderMongoId);
		},
		[setCurrentOrderOIdState],
	);

	const requestWithdraw = useCallback((row) => {
		if (!row) return;
		setWithdrawTarget(row);
	}, []);

	const handleWithdrawConfirm = useCallback(async () => {
		if (!withdrawTarget) return;
		try {
			if (isCustomerOrQuotaEntry(withdrawTarget)) {
				const approvalId = withdrawTarget.approval?._id || withdrawTarget._id;
				await withdrawApprovalApplication(approvalId).unwrap();
				toast.success('已撤回，可在审批信息里再次编辑');
			} else {
				markLocalOrderMutation(withdrawTarget._id);
				await withdrawApplyingOrder(withdrawTarget._id).unwrap();
				toast.success('已撤回，订单已保存为草稿');
			}
			setWithdrawTarget(null);
			setCustomerApplyInfo(null);
			setQuotaApplyInfo(null);
			setIsOpenPendingModal(false);
			refetchApplyingOrders?.();
			socket.emit('refresh processing orderlist');
		} catch (error) {
			toast.error(error?.data?.message || error?.error || '撤回失败，请刷新后重试');
		}
	}, [refetchApplyingOrders, withdrawApprovalApplication, withdrawApplyingOrder, withdrawTarget]);

	const formatDateTime = useCallback((date) => moment(date).format('YYYY-MM-DD HH:mm'), []);
	const isWithdrawing = isWithdrawingOrder || isWithdrawingApproval;
	const withdrawLabel = isCustomerOrQuotaEntry(withdrawTarget)
		? withdrawTarget?.approval?.title || withdrawTarget?.title || (withdrawTarget?.entryKind === 'quota' ? '该额度申请' : '该客户申请')
		: withdrawTarget?.orderId || '该订单';
	const withdrawBody = isCustomerOrQuotaEntry(withdrawTarget)
		? `确定撤回「${withdrawLabel}」吗？撤回后可在审批信息里再次编辑。`
		: `确定撤回「${withdrawLabel}」吗？撤回后将保存为草稿，审批人待办中不再显示。`;

	const columns = useMemo(
		() => [
			{
				accessorKey: 'orderId',
				header: '编号 / 事项',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: { className: 'fw-semibold text-start' },
				},
				cell: ({ row: { original } }) => {
					const { orderId, _id, entryKind, approval } = original;
					if (entryKind === 'customer' || entryKind === 'quota' || entryKind === 'finalCost') {
						const kind = getPendingApprovalKind(original);
						const applyQuota = entryKind === 'quota' ? approval?.detail?.applyQuota : null;
						const amountText = applyQuota != null && applyQuota !== '' ? formatCurrency(applyQuota) : '';
						const rawReason = entryKind === 'quota' ? String(approval?.detail?.rawReason || '').trim() : '';
						const customerCityCode = entryKind === 'customer' ? String(approval?.detail?.newCustomerCity || '').trim() : '';
						const customerCity = customerCityCode ? getCityNameByCode(customerCityCode) : '';
						return (
							<button
								type='button'
								className={`link-${PENDING_TAB_BADGE.applying} text-decoration-none border-0 bg-transparent p-0 fw-semibold text-start`}
								onClick={() => {
									if (entryKind === 'quota') openQuotaApplyInfo(original);
									else if (entryKind === 'finalCost') openFinalCostApplyInfo(original);
									else openCustomerApplyInfo(original);
								}}
							>
								<span className='d-flex flex-column align-items-start gap-1'>
									<span className='pending-approval-kind-line gap-2'>
										<PendingThemeBadge bg={kind.bg} text={kind.label} pill className='flex-shrink-0' />
										<span className='pending-approval-kind-subject' title={kind.subject || undefined}>
											<PendingKeywordHighlight text={kind.subject} />
										</span>
									</span>
									{amountText ? <span className='fs-11 fw-normal text-700'>申请额度 {amountText}</span> : null}
									{rawReason ? (
										<span className='fs-11 fw-normal text-600 text-wrap' style={{ maxWidth: '18rem', whiteSpace: 'normal' }}>
											说明：
											<PendingKeywordHighlight text={rawReason} />
										</span>
									) : null}
									{customerCity ? (
										<span className='fs-11 fw-normal text-600'>
											城市：
											<PendingKeywordHighlight text={customerCity} />
										</span>
									) : null}
								</span>
							</button>
						);
					}
					const kind = getPendingApprovalKind(original);
					const triggerLines = kind.label === '成本修正' ? [] : getApprovalTriggerSummaryLines(original.approval);
					return (
						<div className='d-flex flex-column align-items-start gap-1'>
							<div className='pending-approval-kind-line gap-2 flex-nowrap'>
								<PendingThemeBadge bg={kind.bg} text={kind.label} pill className='flex-shrink-0' />
								<PendingOrderIdCell
									orderId={orderId}
									orderMongoId={_id}
									badgeVariant={PENDING_TAB_BADGE.applying}
									onOrderClick={openApplyingModal}
									order={original}
									fullWidth={false}
								/>
							</div>
							{triggerLines.length > 0 ? (
								<span className='fs-11 fw-normal text-600 text-wrap' style={{ maxWidth: '18rem', whiteSpace: 'normal' }}>
									{triggerLines.map((line, index) => (
										<span key={line}>
											{index > 0 ? '；' : null}
											<PendingKeywordHighlight text={line} />
										</span>
									))}
								</span>
							) : null}
						</div>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => (
					<span className={`link-${PENDING_TAB_BADGE.applying}`}>
						<PendingKeywordHighlight text={original.customerInfo?.customerName} />
					</span>
				),
			},
			{
				id: 'approvalStatus',
				accessorFn: (row) =>
					isStandaloneApprovalEntry(row) ? '审批中' : getPendingApprovalStatusLabel(row.approvalStatus) || '-',
				header: '审批状态',
				meta: { headerProps: { className: 'pe-3 text-900' } },
				cell: ({ row: { original } }) => {
					if (isStandaloneApprovalEntry(original)) {
						return (
							<PendingListStatusBadge
								bg={PENDING_TAB_BADGE.applying}
								text='审批中'
								icon='paper-plane'
								iconProps={{ beatFade: true }}
							/>
						);
					}
					const config = getPendingListStatusBadgeProps(
						original.approvalStatus,
						PENDING_TAB_BADGE.applying,
						'approval',
					);
					return config ? (
						<PendingListStatusBadge bg={config.bg} text={config.text} icon='paper-plane' iconProps={{ beatFade: true }} />
					) : (
						'-'
					);
				},
			},
			{
				id: 'saleName',
				accessorFn: (row) => {
					if (row?.entryKind === 'customer' || row?.entryKind === 'quota') return row?.createdUser?.name || '';
					return row?.saleUserInfo?.name || row?.saleUser?.name || '';
				},
				header: '销售员',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ getValue }) => <span className='fs-11'>{getValue() || '-'}</span>,
			},
			{
				id: 'assistantName',
				accessorFn: (row) => {
					if (row?.entryKind === 'customer' || row?.entryKind === 'quota' || row?.entryKind === 'finalCost') return row?.assistantUser?.name || '';
					return row?.assistantUserInfo?.name || row?.assistantUser?.name || '';
				},
				header: '销售助理',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ getValue }) => <span className='fs-11'>{getValue() || '-'}</span>,
			},
			{
				id: 'applicantName',
				accessorFn: (row) => getPendingListApplicant(row)?.name || '',
				header: '申请人',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => {
					const applicant = getPendingListApplicant(original);
					return (
						<span className={isPendingListApplicantHighlighted(original) ? 'fs-11 text-primary' : 'fs-11'}>{applicant?.name || '-'}</span>
					);
				},
			},
			buildPendingApplyAtColumn(formatDateTime, '提交时间'),
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					headerProps: { className: 'text-end text-900' },
					cellProps: { className: 'text-end py-2' },
				},
				cell: ({ row: { original } }) => {
					if (original.entryKind === 'finalCost') {
						return (
							<CardDropdown drop='start'>
								<div className='py-2'>
									<Dropdown.Item as='button' type='button' onClick={() => openFinalCostApplyInfo(original)}>
										查看
									</Dropdown.Item>
								</div>
							</CardDropdown>
						);
					}
					if (isCustomerOrQuotaEntry(original)) {
						const showWithdraw = canWithdrawApproval(original.approval, userInfo);
						return (
							<CardDropdown drop='start'>
								<div className='py-2'>
									<Dropdown.Item
										as='button'
										type='button'
										onClick={() => (original.entryKind === 'quota' ? openQuotaApplyInfo(original) : openCustomerApplyInfo(original))}
									>
										查看
									</Dropdown.Item>
									{showWithdraw ? (
										<Dropdown.Item as='button' type='button' onClick={() => requestWithdraw(original)}>
											撤回
										</Dropdown.Item>
									) : null}
								</div>
							</CardDropdown>
						);
					}
					const showWithdraw = canWithdrawApplyingOrder(original, userInfo);
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item as='button' type='button' onClick={() => openApplyingModal(original._id)}>
									查看
								</Dropdown.Item>
								{showWithdraw ? (
									<Dropdown.Item as='button' type='button' onClick={() => requestWithdraw(original)}>
										撤回
									</Dropdown.Item>
								) : null}
							</div>
						</CardDropdown>
					);
				},
			},
		],
		[formatDateTime, openApplyingModal, openCustomerApplyInfo, openFinalCostApplyInfo, openQuotaApplyInfo, requestWithdraw, userInfo],
	);

	const table = useAdvanceTable({
		data: displayOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			pagination: { pageIndex: restoredPageIndex },
			sorting: [{ id: 'applyAt', desc: true }],
		},
	});

	if (isLoadingApplyingOrders) {
		return <PendingListLoadingState />;
	}

	const showFooter = applyingOrders.length > 0 || hasKeyword;

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar>
					{displayOrders.length === 0 ? (
						<PendingListEmptyState
							emptyTitle={hasKeyword ? '无匹配项' : '暂无申请'}
							emptyDescription={
								hasKeyword ? `当前待办中没有包含「${keyword}」的记录。` : '目前没有进行中的订单、客户、额度或成本修正申请。'
							}
						/>
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={displayOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<ApplyingModal
				setIsOpenPendingModal={setIsOpenPendingModal}
				isOpenPendingModal={isOpenPendingModal}
				onRequestWithdraw={
					canWithdrawApplyingOrder(order, userInfo)
						? () => {
								const row = applyingOrders.find((item) => !isStandaloneApprovalEntry(item) && String(item._id) === String(order?._id));
								requestWithdraw(row || order);
							}
						: undefined
				}
			/>
			<QuotaApplyInfoModal
				show={Boolean(quotaApplyInfo)}
				onHide={() => setQuotaApplyInfo(null)}
				approval={quotaApplyInfo?.approval}
				onWithdraw={
					canWithdrawApproval(quotaApplyInfo?.approval, userInfo)
						? () => {
								const row = quotaApplyInfo?.row;
								setQuotaApplyInfo(null);
								if (row) requestWithdraw(row);
							}
						: undefined
				}
			/>
			<FinalCostApplyInfoModal
				show={Boolean(finalCostApplyInfo)}
				onHide={() => setFinalCostApplyInfo(null)}
				approval={finalCostApplyInfo?.approval}
				onResolved={() => {
					setFinalCostApplyInfo(null);
					refetchApplyingOrders?.();
				}}
			/>
			<CustomerApplyInfoModal
				show={Boolean(customerApplyInfo)}
				onHide={() => setCustomerApplyInfo(null)}
				approval={customerApplyInfo?.approval}
				onWithdraw={
					canWithdrawApproval(customerApplyInfo?.approval, userInfo)
						? () => {
								const row = customerApplyInfo?.row;
								setCustomerApplyInfo(null);
								if (row) requestWithdraw(row);
							}
						: undefined
				}
			/>
			<SystemAlert
				title='撤回确认'
				bodyText={withdrawBody}
				cancelText='返回'
				confirmText='确定撤回'
				confirmColor='primary'
				confirmWaiting={isWithdrawing}
				modalShow={Boolean(withdrawTarget)}
				setModalShow={(show) => {
					if (!show) setWithdrawTarget(null);
				}}
				handleConfirm={handleWithdrawConfirm}
			/>
		</>
	);
};

export default ApplyingOrderPendings;
